import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, TrendingUp, ClipboardList, Heart, Calendar } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const CaregiverDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddChild, setShowAddChild] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [childForm, setChildForm] = useState({
    name: '',
    date_of_birth: '',
    gender: 'male',
    age_months: 0,
    guardian_name: '',
    location: '',
    state: '',
    district: '',
    taluk: '',
    city_village: '',
    consent_given: false
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchDashboard();
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/states`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStates(data.states || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistricts = async (state) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/districts/${encodeURIComponent(state)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDistricts(data.districts || []);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchTaluks = async (district) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/taluks/${encodeURIComponent(district)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTaluks(data.taluks || []);
      }
    } catch (error) {
      console.error('Error fetching taluks:', error);
    }
  };

  const handleStateChange = (state) => {
    setChildForm({ ...childForm, state, district: '', taluk: '', city_village: '' });
    setDistricts([]);
    setTaluks([]);
    if (state) {
      fetchDistricts(state);
    }
  };

  const handleDistrictChange = (district) => {
    setChildForm({ ...childForm, district, taluk: '', city_village: '' });
    setTaluks([]);
    if (district) {
      fetchTaluks(district);
    }
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, childrenRes] = await Promise.all([
        api.getCaregiverDashboard(),
        api.getChildren()
      ]);
      setDashboard(dashRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    if (!childForm.consent_given) {
      alert(t('consent.required'));
      return;
    }
    try {
      await api.createChild(childForm);
      setShowAddChild(false);
      setChildForm({
        name: '',
        date_of_birth: '',
        gender: 'male',
        age_months: 0,
        guardian_name: '',
        location: '',
        state: '',
        district: '',
        taluk: '',
        city_village: '',
        consent_given: false
      });
      fetchDashboard();
    } catch (error) {
      console.error('Error adding child:', error);
      alert('Failed to add child. Please try again.');
    }
  };

  const getAgeDisplay = (months) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years > 0) {
      return `${years}y ${remainingMonths}m`;
    }
    return `${months} months`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="caregiver-dashboard">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="dashboard-title">
            {t('common.welcome')}, {user.name}
          </h1>
          <p className="text-lg text-[#4B5563]">
            {t('caregiver.trackProgress')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-children">
            <div className="flex items-center justify-between mb-2">
              <Heart className="text-[#10B981]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.total_children || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.children')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-screenings">
            <div className="flex items-center justify-between mb-2">
              <ClipboardList className="text-[#3B82F6]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.recent_screenings || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.screenings')}</p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="stat-interventions">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="text-[#F59E0B]" size={32} />
              <span className="text-3xl font-bold text-[#111827] font-['Nunito']">{dashboard?.active_interventions || 0}</span>
            </div>
            <p className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider">{t('dashboard.interventions')}</p>
          </div>
        </div>

        {/* Children Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-[#111827] font-['Nunito']" data-testid="children-section-title">{t('child.myChildren')}</h2>
            <button
              onClick={() => setShowAddChild(!showAddChild)}
              data-testid="add-child-button"
              className="flex items-center space-x-2 px-6 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all active:scale-95 shadow-md"
            >
              <Plus size={20} />
              <span>{t('child.addChild')}</span>
            </button>
          </div>

          {/* Add Child Form */}
          {showAddChild && (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-6 fade-in" data-testid="add-child-form">
              <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('child.addChild')}</h3>
              <form onSubmit={handleAddChild} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.childName')} *</label>
                    <input
                      type="text"
                      data-testid="child-name-input"
                      value={childForm.name}
                      onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.dateOfBirth')} *</label>
                    <input
                      type="date"
                      data-testid="child-dob-input"
                      value={childForm.date_of_birth}
                      onChange={(e) => setChildForm({ ...childForm, date_of_birth: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.gender')} *</label>
                    <select
                      data-testid="child-gender-select"
                      value={childForm.gender}
                      onChange={(e) => setChildForm({ ...childForm, gender: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                    >
                      <option value="male">{t('child.male')}</option>
                      <option value="female">{t('child.female')}</option>
                      <option value="other">{t('child.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.ageMonths')} *</label>
                    <input
                      type="number"
                      data-testid="child-age-input"
                      value={childForm.age_months}
                      onChange={(e) => setChildForm({ ...childForm, age_months: parseInt(e.target.value) })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      min="0"
                      max="72"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.guardianName')} *</label>
                    <input
                      type="text"
                      data-testid="guardian-name-input"
                      value={childForm.guardian_name}
                      onChange={(e) => setChildForm({ ...childForm, guardian_name: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.location')}</label>
                    <input
                      type="text"
                      data-testid="child-location-input"
                      value={childForm.location}
                      onChange={(e) => setChildForm({ ...childForm, location: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      placeholder="e.g., Mumbai, Maharashtra"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.state')}</label>
                    <select
                      data-testid="child-state-select"
                      value={childForm.state}
                      onChange={(e) => handleStateChange(e.target.value)}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                    >
                      <option value="">{t('admin.selectState')}</option>
                      {states.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.district')}</label>
                    <select
                      data-testid="child-district-select"
                      value={childForm.district}
                      onChange={(e) => handleDistrictChange(e.target.value)}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      disabled={!childForm.state}
                    >
                      <option value="">{t('admin.selectDistrict')}</option>
                      {districts.map((district) => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.taluk')}</label>
                    <input
                      type="text"
                      data-testid="child-taluk-input"
                      value={childForm.taluk}
                      onChange={(e) => setChildForm({ ...childForm, taluk: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      placeholder={t('child.enterTaluk')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">{t('child.cityVillage')}</label>
                    <input
                      type="text"
                      data-testid="child-city-village-input"
                      value={childForm.city_village}
                      onChange={(e) => setChildForm({ ...childForm, city_village: e.target.value })}
                      className="w-full h-14 text-lg px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                      placeholder={t('child.enterCityVillage')}
                    />
                  </div>
                </div>

                {/* Consent Checkbox */}
                <div className="bg-[#F0FDF4] border-2 border-[#86EFAC] rounded-xl p-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="consent-checkbox"
                      checked={childForm.consent_given}
                      onChange={(e) => setChildForm({ ...childForm, consent_given: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-2 border-[#0F4C81] text-[#0F4C81] focus:ring-[#0F4C81] focus:ring-2"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-[#111827] text-sm block mb-1">{t('consent.title')}</span>
                      <span className="text-sm text-[#4B5563]">{t('consent.description')}</span>
                    </div>
                  </label>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    data-testid="submit-child-button"
                    className="px-8 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all active:scale-95 shadow-md"
                  >
                    {t('child.addChild')}
                  </button>
                  <button
                    type="button"
                    data-testid="cancel-add-child-button"
                    onClick={() => setShowAddChild(false)}
                    className="px-8 py-3 bg-gray-200 text-[#111827] rounded-full font-bold hover:bg-gray-300 transition-all active:scale-95"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Children List */}
          {children.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border-2 border-slate-100" data-testid="no-children-message">
              <Heart className="mx-auto text-[#9CA3AF] mb-4" size={64} />
              <p className="text-lg text-[#4B5563] mb-4">{t('child.noChildren')}</p>
              <button
                onClick={() => setShowAddChild(true)}
                data-testid="add-first-child-button"
                className="px-6 py-3 bg-[#0F4C81] text-white rounded-full font-bold hover:bg-[#0d3d6b] transition-all active:scale-95"
              >
                {t('child.addFirstChild')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child) => (
                <div
                  key={child.id}
                  data-testid={`child-card-${child.id}`}
                  className="rounded-2xl border-2 border-slate-100 bg-white p-6 shadow-sm hover:border-[#0F4C81] hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/child/${child.id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0F4C81] to-[#1e5a9a] rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">{child.name.charAt(0)}</span>
                    </div>
                    <span className="text-xs bg-[#F59E0B] text-black px-3 py-1 rounded-full font-bold">
                      {getAgeDisplay(child.age_months)}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#111827] mb-2 font-['Nunito']">{child.name}</h3>
                  <p className="text-sm text-[#4B5563] mb-1"><span className="font-semibold">{t('child.guardianName')}:</span> {child.guardian_name}</p>
                  {child.location && (
                    <p className="text-sm text-[#4B5563]"><span className="font-semibold">{t('child.location')}:</span> {child.location}</p>
                  )}
                  <div className="mt-4 pt-4 border-t border-slate-200 flex space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/screening/${child.id}`);
                      }}
                      data-testid={`screening-button-${child.id}`}
                      className="flex-1 px-4 py-2 bg-[#3B82F6] text-white rounded-lg font-semibold hover:bg-[#2563EB] transition-colors text-sm"
                    >
                      {t('screening.conductScreening')}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/interventions/${child.id}`);
                      }}
                      data-testid={`intervention-button-${child.id}`}
                      className="flex-1 px-4 py-2 bg-[#10B981] text-white rounded-lg font-semibold hover:bg-[#059669] transition-colors text-sm"
                    >
                      {t('intervention.title')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    
    {/* Copyright Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default CaregiverDashboard;